import { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetch } from '@reveni-io/reveni-ui'
import { ToastContext } from '@reveni-io/reveni-ui'

import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'

import { servicesByViewsMode, viewsModes } from './constants'
import { getDefaultView } from './helpers'

const useViews = ({
  storeId,
  merchantId,
  defaultViews = [],
  type,
  editPermission,
  defaultViewsPermission,
  sharePermission,
}) => {
  const viewsMode = merchantId ? viewsModes.merchant : viewsModes.store
  const services = servicesByViewsMode[viewsMode]
  const entityId = merchantId ? { merchantId } : { storeId }
  const {
    data: views,
    loading,
    fetchData: getViewsRequest,
  } = useFetch(services.getViews, { doInitialRequest: storeId || merchantId, params: { ...entityId, type } })
  const { fetchData: createViewRequest, loading: creatingView } = useFetch(services.createView, {
    doInitialRequest: false,
  })
  const { fetchData: deleteViewRequest } = useFetch(services.deleteView, { doInitialRequest: false })
  const { fetchData: saveViewRequest, loading: savingView } = useFetch(services.saveView, { doInitialRequest: false })
  const [selectedView, setSelectedView] = useState(null)
  const [hasViewChanges, setHasViewChanges] = useState(false)
  const { setToastInfo } = useContext(ToastContext)
  const { t } = useTranslation()
  const { hasPermission, isOwner } = useUserPermissions()
  const canEditViews = !editPermission || hasPermission(editPermission) || Boolean(isOwner())
  const canManageSelectedView = Boolean(selectedView?.is_editable)
  const canViewDefaultViews = !defaultViewsPermission || hasPermission(defaultViewsPermission)
  const canShareViewsWithAllGroups = !sharePermission || hasPermission(sharePermission) || Boolean(isOwner())
  const availableDefaultViews = useMemo(
    () => (canViewDefaultViews ? defaultViews : []),
    [canViewDefaultViews, defaultViews]
  )
  const hasAvailableViews = availableDefaultViews.length > 0 || Boolean(views?.results?.length)

  useEffect(() => {
    const hasViewsLoaded = views?.results
    const shouldSetDefaultView = (!storeId && !merchantId) || hasViewsLoaded

    if (!selectedView && shouldSetDefaultView) {
      const storedViewId = localStorage.getItem(`selectedViewId-${type}`)

      setSelectedView(getDefaultView(views?.results || [], availableDefaultViews, storedViewId))
    }
  }, [availableDefaultViews, type, views, selectedView, storeId, merchantId])

  useEffect(() => {
    if (selectedView) {
      localStorage.setItem(`selectedViewId-${type}`, selectedView.id)
      setHasViewChanges(false)
    }
  }, [selectedView, type])

  const duplicateView = async () => {
    const createdView = await createViewRequest({
      ...entityId,
      name: `${selectedView.name} (copy)`,
      filters: selectedView.filters,
      type,
    })
    const data = await getViewsRequest({ ...entityId, type })
    setSelectedView(data?.results?.find(view => view.id === createdView?.id))
    setToastInfo({ show: true, message: t('Guardado correctamente'), type: 'success' })
  }

  const deleteView = async () => {
    await deleteViewRequest({ ...entityId, viewId: selectedView?.id })
    const data = await getViewsRequest({ ...entityId, type })
    setSelectedView(
      getDefaultView(data?.results || [], availableDefaultViews, localStorage.getItem(`selectedViewId-${type}`))
    )
    setToastInfo({ show: true, message: t('Eliminado correctamente'), type: 'success' })
  }

  const saveView = async ({ name, filters, sharedWithGroups }) => {
    await saveViewRequest({ ...entityId, viewId: selectedView?.id, filters, name, type, sharedWithGroups })
    const data = await getViewsRequest({ ...entityId, type })
    setSelectedView(data?.results?.find(view => view.id === selectedView?.id))
    setToastInfo({ show: true, message: t('Guardado correctamente'), type: 'success' })
  }

  const createView = async ({ name = 'New view', filters, sharedWithGroups }) => {
    const createdView = await createViewRequest({ ...entityId, name, filters, type, sharedWithGroups })
    const data = await getViewsRequest({ ...entityId, type })
    setSelectedView(data?.results?.find(view => view.id === createdView?.id))
    setToastInfo({ show: true, message: t('Guardado correctamente'), type: 'success' })
  }

  return {
    views: views?.results || [],
    fetchingViews: loading,
    selectedView,
    setSelectedView,
    duplicateView,
    deleteView,
    hasViewChanges,
    setHasViewChanges,
    saveView,
    savingView,
    createView,
    creatingView,
    canEditViews,
    canManageSelectedView,
    canViewDefaultViews,
    canShareViewsWithAllGroups,
    hasAvailableViews,
  }
}

export default useViews
